import { Box, Flex, Spinner, Text } from '@chakra-ui/react'
import React, { useCallback, useEffect, useMemo } from 'react'

import { type ReportingApi, ReportingApiProvider } from '../../api'
import {
  ReportingI18nProvider,
  type ReportingTranslationLike,
  useReportingI18n,
} from '../../i18n'
import AgentsPanel, { type AgentsPanelProps } from './components/AgentsPanel'
import CallersTable from './components/CallersTable'
import EmptyState from './components/EmptyState'
import ErrorDisplay from './components/ErrorDisplay'
import QueueMetricsCards from './components/QueueMetricsCards'
import { DEFAULT_RETRY_COUNTDOWN_SECONDS } from './constants'
import { useConsecutiveFailures } from './hooks/useConsecutiveFailures'
import { usePageVisibility } from './hooks/usePageVisibility'
import { useQueueAgents } from './hooks/useQueueAgents'
import { useQueueCalls } from './hooks/useQueueCalls'
import { useQueues } from './hooks/useQueues'
import { useQueueStats } from './hooks/useQueueStats'
import { useRetryCountdown } from './hooks/useRetryCountdown'
import type { QueueDashboardSettings, QueueStats } from './types'

export interface QueueDashboardDetailProps {
  api: ReportingApi
  t?: ReportingTranslationLike
  locale?: string
  selectedPbxId: number
  selectedPbxUuid: string | null
  selectedQueueId: string | null
  settings: QueueDashboardSettings
  onSettingsChange?: (settings: Partial<QueueDashboardSettings>) => void
  isAdmin?: boolean
  /** When true, shows the full metrics cards powered by the aggregate endpoint. */
  enableQueueMetrics?: boolean
  customStatuses?: AgentsPanelProps['customStatuses']
  extensions?: AgentsPanelProps['extensions']
  onQueueLogin?: AgentsPanelProps['onQueueLogin']
  onQueueLogout?: AgentsPanelProps['onQueueLogout']
  onUpdateAgentStatus?: AgentsPanelProps['onUpdateAgentStatus']
  onQueueUpdateError?: AgentsPanelProps['onQueueUpdateError']
  onStatusUpdated?: AgentsPanelProps['onStatusUpdated']
}

const countCallersInQueue = (callers: { state: string }[]): number => {
  return callers.length
}

type QueueDashboardDetailInnerProps = Omit<
  QueueDashboardDetailProps,
  'api' | 't' | 'locale'
>

export const QueueDashboardDetailInner: React.FC<
  QueueDashboardDetailInnerProps
> = ({
  selectedPbxId,
  selectedPbxUuid,
  selectedQueueId,
  settings,
  onSettingsChange,
  isAdmin = false,
  enableQueueMetrics = false,
  customStatuses = [],
  extensions = [],
  onQueueLogin,
  onQueueLogout,
  onUpdateAgentStatus,
  onQueueUpdateError,
  onStatusUpdated,
}) => {
  const { text } = useReportingI18n()
  const isPageVisible = usePageVisibility()

  const validPbxId = selectedPbxId ?? 0
  const pbxUuid = selectedPbxUuid ?? null

  // Fetch queues to get the extension number for the selected queue
  const { data: queues = [] } = useQueues({
    pbxId: validPbxId,
    options: {
      enabled: !!validPbxId,
    },
  })

  // Find the selected queue and get its extension number
  const selectedQueue = useMemo(
    () => queues.find((q) => q.id === selectedQueueId),
    [queues, selectedQueueId],
  )
  const queueNumber = selectedQueue?.extensionNumber ?? null

  const {
    consecutiveFailures: _consecutiveFailures,
    shouldStopPolling,
    recordSuccess,
    recordFailure,
    reset: resetFailures,
  } = useConsecutiveFailures()

  const { countdown, isCountingDown, startCountdown, stopCountdown } =
    useRetryCountdown({
      defaultSeconds: DEFAULT_RETRY_COUNTDOWN_SECONDS,
    })

  const shouldPoll =
    isPageVisible && !!queueNumber && !!pbxUuid && !shouldStopPolling

  const {
    data: callsData,
    isLoading: isLoadingCalls,
    error: callsError,
    refetch: refetchCalls,
    isRefetching: isRefetchingCalls,
  } = useQueueCalls({
    pbxUuid,
    queueNumber,
    refreshInterval: settings.refreshInterval,
    enabled: shouldPoll,
  })

  const {
    data: agentsData,
    isLoading: isLoadingAgents,
    error: agentsError,
    refetch: refetchAgents,
    isRefetching: isRefetchingAgents,
  } = useQueueAgents({
    pbxUuid,
    queueNumber,
    refreshInterval: settings.refreshInterval,
    enabled: shouldPoll,
  })

  const {
    data: statsData,
    isLoading: isLoadingStats,
    error: statsError,
    refetch: refetchStats,
  } = useQueueStats({
    pbxId: validPbxId,
    queueId: selectedQueueId,
    queueNumber,
    sampleHistoryMinutes: settings.sampleHistoryMinutes,
    refreshInterval: settings.refreshInterval,
    enabled: shouldPoll && enableQueueMetrics,
  })

  const hasError = !!(
    callsError ||
    agentsError ||
    (enableQueueMetrics && statsError)
  )
  const isRefetching = isRefetchingCalls || isRefetchingAgents

  const errorMessage = useMemo(() => {
    const error =
      callsError || agentsError || (enableQueueMetrics ? statsError : null)
    if (!error) return ''

    if (
      error.message?.includes('timeout') ||
      error.message?.includes('Timeout')
    ) {
      return text(
        'queueDashboard.error.timeout',
        'Request timed out. The server is taking too long to respond.',
      )
    }
    if (
      error.message?.includes('network') ||
      error.message?.includes('Network')
    ) {
      return text(
        'queueDashboard.error.network',
        'Unable to connect to server. Please check your connection.',
      )
    }
    if (error.message?.includes('500') || error.message?.includes('Server')) {
      return text(
        'queueDashboard.error.server',
        'Server error occurred. Please try again later.',
      )
    }
    if (error.message?.includes('404')) {
      return text(
        'queueDashboard.error.notFound',
        'Queue not found. Please select a different queue.',
      )
    }

    return text(
      'queueDashboard.error.generic',
      'Unable to load queue data. Please try again.',
    )
  }, [callsError, agentsError, statsError, enableQueueMetrics, text])

  useEffect(() => {
    if (hasError) {
      recordFailure()
      if (!isCountingDown && !shouldStopPolling) {
        startCountdown()
      }
    } else if (callsData || agentsData) {
      recordSuccess()
      stopCountdown()
    }
  }, [
    hasError,
    callsData,
    agentsData,
    recordFailure,
    recordSuccess,
    isCountingDown,
    shouldStopPolling,
    startCountdown,
    stopCountdown,
  ])

  useEffect(() => {
    resetFailures()
    stopCountdown()
  }, [selectedQueueId, resetFailures, stopCountdown])

  const handleRetry = useCallback(() => {
    stopCountdown()
    resetFailures()
    refetchCalls()
    refetchAgents()
    if (enableQueueMetrics) refetchStats()
  }, [
    stopCountdown,
    resetFailures,
    refetchCalls,
    refetchAgents,
    refetchStats,
    enableQueueMetrics,
  ])

  const callers = callsData?.calls ?? []
  const activeAgents = agentsData?.activeAgents ?? []
  const inactiveAgents = agentsData?.inactiveAgents ?? []
  const callersInQueue = useMemo(() => countCallersInQueue(callers), [callers])

  const stats: QueueStats | null = statsData?.stats ?? null

  const isInitialLoading =
    selectedQueueId &&
    (isLoadingCalls ||
      isLoadingAgents ||
      (enableQueueMetrics && isLoadingStats)) &&
    !callsData &&
    !agentsData &&
    !hasError

  const handleAgentRefresh = () => {
    refetchAgents()
    refetchCalls()
  }

  if (!selectedQueueId) {
    return <EmptyState />
  }

  if (isInitialLoading) {
    return (
      <Flex
        direction="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        minHeight="300px"
        gap="16px"
        data-testid="queue-dashboard-loading"
      >
        <Spinner size="xl" color="fg.muted" />
        <Text color="fg.muted" fontSize="0.875rem">
          {text('queueDashboard.loading', 'Loading queue data...')}
        </Text>
      </Flex>
    )
  }

  if (hasError && !callsData && !agentsData) {
    return (
      <Flex
        direction="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        minHeight="300px"
        padding="32px"
        data-testid="queue-dashboard-error-state"
      >
        <ErrorDisplay
          message={errorMessage}
          isPersistent={shouldStopPolling}
          countdown={countdown}
          onRetry={handleRetry}
          isRetrying={isRefetching}
        />
      </Flex>
    )
  }

  return (
    <Flex
      direction="column"
      gap="24px"
      width="100%"
      minWidth="500px"
      data-testid="queue-dashboard-detail"
      css={{
        // Enable container queries - components inside can respond to this container's width
        containerType: 'inline-size',
        containerName: 'queue-dashboard',
      }}
    >
      {hasError && (callsData || agentsData) && (
        <ErrorDisplay
          message={errorMessage}
          isPersistent={shouldStopPolling}
          countdown={countdown}
          onRetry={handleRetry}
          isRetrying={isRefetching}
        />
      )}

      {enableQueueMetrics ? (
        <QueueMetricsCards
          stats={stats}
          callersInQueue={callersInQueue}
          isLoading={isLoadingStats && !statsData}
          isLoadingCallers={isLoadingCalls && !callsData}
          settings={settings}
          onSettingsChange={onSettingsChange}
        />
      ) : null}

      <Flex gap="24px" width="100%" alignItems="flex-start">
        {!enableQueueMetrics && (
          <Box
            bg="bg.panel"
            borderRadius="8px"
            border="1px solid"
            borderColor="border.default"
            padding="16px"
            minWidth="140px"
            display="flex"
            flexDirection="column"
            alignSelf="stretch"
          >
            <Text fontSize="0.75rem" fontWeight="500" color="fg.muted">
              {text(
                'queueDashboard.metrics.callersInQueue',
                'Callers In Queue',
              )}
            </Text>
            <Flex flex="1" alignItems="center" justifyContent="center">
              <Text
                fontSize="5rem"
                fontWeight="700"
                lineHeight="1"
                color="blue.solid"
                data-testid="callers-in-queue-count"
              >
                {isLoadingCalls && !callsData ? '-' : callersInQueue}
              </Text>
            </Flex>
          </Box>
        )}

        <CallersTable
          callers={callers}
          holdAlertThreshold={settings.holdAlertThreshold}
          isLoading={isLoadingCalls && !callsData}
          settings={settings}
          onSettingsChange={onSettingsChange}
        />
      </Flex>

      <AgentsPanel
        activeAgents={activeAgents}
        inactiveAgents={inactiveAgents}
        isLoading={isLoadingAgents && !agentsData}
        onRefresh={handleAgentRefresh}
        customStatuses={customStatuses}
        extensions={extensions}
        isAdmin={isAdmin}
        onQueueLogin={onQueueLogin}
        onQueueLogout={onQueueLogout}
        onUpdateAgentStatus={onUpdateAgentStatus}
        onQueueUpdateError={onQueueUpdateError}
        onStatusUpdated={onStatusUpdated}
      />
    </Flex>
  )
}

export const QueueDashboardDetail: React.FC<QueueDashboardDetailProps> = ({
  api,
  t,
  locale,
  ...rest
}) => (
  <ReportingApiProvider api={api}>
    <ReportingI18nProvider t={t} locale={locale}>
      <QueueDashboardDetailInner {...rest} />
    </ReportingI18nProvider>
  </ReportingApiProvider>
)

export default QueueDashboardDetail
